import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Search } from "lucide-react";

export function useAdminData(loader, initialFilters = {}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState(initialFilters);

  const load = useCallback(async (p, f) => {
    const params = new URLSearchParams({ page: String(p), page_size: "25" });
    Object.entries(f).forEach(([k, v]) => { if (v) params.set(k, v); });
    try { setData(await loader(`?${params.toString()}`)); setError(null); }
    catch (e) { setError(e.message); setData({ items: [], total: 0, page: 1, page_size: 25 }); if (e.status !== 403) toast.error(e.message); }
  }, [loader]);

  useEffect(() => { setPage(1); load(1, filters); }, [filters]); // eslint-disable-line
  useEffect(() => { if (page !== 1) load(page, filters); }, [page]); // eslint-disable-line

  return { data, error, page, setPage, filters, setFilters, reload: () => load(page, filters) };
}

export const act = async (fn, okMsg, reload) => {
  try { await fn(); toast.success(okMsg); reload(); }
  catch (e) { toast.error(e.message); }
};

export function SearchBox({ value, onChange, onSearch, placeholder, testid }) {
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSearch(); }} className="relative max-w-sm" role="search">
      <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
      <input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
             className="input-dark !pl-11" placeholder={placeholder} aria-label={placeholder} />
    </form>
  );
}

export function FilterSelect({ value, onChange, options, label, testid }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} aria-label={label} data-testid={testid}
            className="input-dark !w-auto !h-10 !py-0 text-xs">
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
}

export function Pager({ page, total, pageSize, onPage, testid }) {
  const pages = Math.max(1, Math.ceil((total || 0) / pageSize));
  return (
    <div className="mt-4 flex items-center justify-between" data-testid={testid}>
      <p className="text-xs text-zinc-500">Page {page} of {pages} · {total} total</p>
      <div className="flex gap-2">
        <button disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid={`${testid}-prev`}
                className="btn-secondary !px-3 !py-1.5 text-xs disabled:opacity-40">Prev</button>
        <button disabled={page >= pages} onClick={() => onPage(page + 1)} data-testid={`${testid}-next`}
                className="btn-secondary !px-3 !py-1.5 text-xs disabled:opacity-40">Next</button>
      </div>
    </div>
  );
}

export function TableCard({ testid, head, children, empty }) {
  return (
    <div className="card-dark overflow-x-auto" data-testid={testid}>
      <table className="w-full text-sm min-w-[640px]">
        <thead><tr className="text-left text-xs text-zinc-500 border-b border-[#27272A]">{head}</tr></thead>
        <tbody>{children}</tbody>
      </table>
      {empty && <p className="p-8 text-center text-sm text-zinc-500">{empty}</p>}
    </div>
  );
}

export const TH = ({ children, right }) => <th className={`p-4 ${right ? "text-right" : ""}`}>{children}</th>;
